import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtErr } from "../api";
import { useI18n } from "../i18n";
import { Languages, ExternalLink, Check } from "lucide-react";
import ImageUploader, { GalleryUploader } from "../components/ImageUploader";

const CSV = (s) => (s ? s.split(",").map((x) => x.trim()).filter(Boolean) : []);
const TO_CSV = (a) => (a || []).join(", ");

// iter31 — mirror of backend FREE_EMAIL_DOMAINS (compute_auto_verified
// rule). Kept in lockstep with /app/backend/server.py FREE_EMAIL_DOMAINS.
// We compute the verification state locally from the save response so
// the toast can render immediately without a separate /dashboard/stats
// round-trip — fast feedback loop for the owner.
const FREE_EMAIL_DOMAINS = new Set([
  "gmail.com", "outlook.com", "hotmail.com", "yahoo.com", "live.com",
]);

function getVerificationState(profile) {
  const email = (profile?.contact_email || "").trim().toLowerCase();
  const domain = email.includes("@") ? email.split("@").pop() : "";
  const corporateEmail = !!(domain && !FREE_EMAIL_DOMAINS.has(domain));
  const phone = !!(profile?.contact_phone || "").trim();
  const website = !!(profile?.website || "").trim();
  const companyName = !!(profile?.company_name || "").trim();
  const complete = [corporateEmail, phone, website, companyName].filter(Boolean).length;
  return {
    complete,
    total: 4,
    allDone: complete === 4,
    fields: { corporateEmail, phone, website, companyName },
  };
}

export default function ProfileEditor() {
  const { t, lang } = useI18n();
  const [p, setP] = useState(null);
  const [verifToast, setVerifToast] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(null);
  // iter31 — retain the dismissal timer id so rapid back-to-back saves
  // don't let orphaned setTimeouts from prior saves prematurely null
  // out a fresh toast. Cleared at the top of every save() and on unmount.
  const dismissTimerRef = useRef(null);
  useEffect(() => () => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
  }, []);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/manufacturers/me");
      setP({
        ...data,
        capabilitiesCsv: TO_CSV(data.capabilities),
        certificationsCsv: TO_CSV(data.certifications),
        industriesCsv: TO_CSV(data.industries),
        galleryText: (data.gallery || []).join("\n"),
      });
    })();
  }, []);

  if (!p) return <div className="container-cd py-20 overline">{t("common.loading")}</div>;

  function set(k, v) { setP((s) => ({ ...s, [k]: v })); }

  async function translate(dir) {
    setTranslating(dir);
    try {
      if (dir === "es_en") {
        const r1 = await api.post("/translate", { text: p.tagline_es || "", source_lang: "es", target_lang: "en" });
        const r2 = await api.post("/translate", { text: p.description_es || "", source_lang: "es", target_lang: "en" });
        setP((s) => ({ ...s, tagline_en: r1.data.translated, description_en: r2.data.translated }));
      } else {
        const r1 = await api.post("/translate", { text: p.tagline_en || "", source_lang: "en", target_lang: "es" });
        const r2 = await api.post("/translate", { text: p.description_en || "", source_lang: "en", target_lang: "es" });
        setP((s) => ({ ...s, tagline_es: r1.data.translated, description_es: r2.data.translated }));
      }
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
    } finally {
      setTranslating(null);
    }
  }

  async function save({ publish }) {
    setSaving(true); setErr(""); setVerifToast(null);
    // Cancel any pending dismissal from an earlier save so its
    // setTimeout doesn't null out the toast we're about to set.
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    try {
      const payload = {
        company_name: p.company_name,
        tagline_es: p.tagline_es || "",
        tagline_en: p.tagline_en || "",
        description_es: p.description_es || "",
        description_en: p.description_en || "",
        capabilities: CSV(p.capabilitiesCsv),
        certifications: CSV(p.certificationsCsv),
        industries: CSV(p.industriesCsv),
        city: p.city || "",
        state: p.state || "",
        country: p.country || "MX",
        year_founded: p.year_founded ? Number(p.year_founded) : null,
        employees: p.employees || null,
        plant_size_sqm: p.plant_size_sqm ? Number(p.plant_size_sqm) : null,
        website: p.website || "",
        contact_email: p.contact_email || "",
        contact_phone: p.contact_phone || "",
        logo_url: p.logo_url || "",
        cover_image_url: p.cover_image_url || "",
        gallery: (p.galleryText || "").split("\n").map((s) => s.trim()).filter(Boolean),
        languages: p.languages || ["ES", "EN"],
        published: publish !== undefined ? publish : !!p.published,
        legal_name: p.legal_name || "",
        rfc: p.rfc || "",
        immex_number: p.immex_number || "",
        padron_number: p.padron_number || "",
        duns_number: p.duns_number || "",
      };
      const { data } = await api.put("/manufacturers/me", payload);
      setP({
        ...data,
        capabilitiesCsv: TO_CSV(data.capabilities),
        certificationsCsv: TO_CSV(data.certifications),
        industriesCsv: TO_CSV(data.industries),
        galleryText: (data.gallery || []).join("\n"),
      });
      // iter31 — compute verification progress from the just-saved
      // server response (NOT the local form state — server may have
      // normalized the fields). The toast lives 6 seconds for the
      // "more to go" case and 8 seconds for the celebratory "all done"
      // case, so owners have time to read but the message clears on
      // the next save automatically.
      const v = getVerificationState(data);
      setVerifToast({
        complete: v.complete,
        total: v.total,
        allDone: v.allDone,
        published: publish !== undefined ? publish : data.published,
      });
      const ttl = v.allDone ? 8000 : 6000;
      dismissTimerRef.current = setTimeout(() => {
        setVerifToast(null);
        dismissTimerRef.current = null;
      }, ttl);
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="container-cd py-12" data-testid="profile-editor-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline">EDIT · MICROSITE</div>
          <h1 className="font-display text-5xl md:text-6xl mt-3 leading-[0.9]">{t("profile.title")}</h1>
        </div>
        <div className="flex gap-3">
          {p.published && p.slug && (
            <Link to={`/m/${p.slug}`} className="btn btn-ghost" target="_blank" data-testid="view-public-link">
              <ExternalLink size={14} /> /m/{p.slug}
            </Link>
          )}
          <button className="btn btn-dark" disabled={saving} onClick={() => save({ publish: !p.published })} data-testid="publish-btn">
            {p.published ? t("common.unpublished") : t("profile.publish")}
          </button>
          <button className="btn btn-primary" disabled={saving} onClick={() => save({})} data-testid="save-btn">
            {saving ? "…" : t("profile.save")}
          </button>
        </div>
      </div>

      {verifToast ? (
        <VerificationToast
          complete={verifToast.complete}
          total={verifToast.total}
          allDone={verifToast.allDone}
          published={verifToast.published}
          lang={lang}
        />
      ) : null}
      {err ? (
        <div className="cd-card p-3 mb-6 font-mono text-xs" style={{ background: "var(--red)", color: "white" }} data-testid="err-msg">{err}</div>
      ) : null}

      <div className="grid md:grid-cols-12 gap-10">
        {/* LEFT */}
        <div className="md:col-span-7 space-y-8">
          <Section label="IDENTITY">
            <FieldBox label={t("profile.company_name")} value={p.company_name} onChange={(v) => set("company_name", v)} testid="f-company_name" required />
            <div className="grid grid-cols-2 gap-4">
              <ImageUploader
                label={t("profile.logo_url")}
                value={p.logo_url}
                onChange={(v) => set("logo_url", v)}
                aspect="square"
                testid="f-logo"
              />
              <ImageUploader
                label={t("profile.cover_url")}
                value={p.cover_image_url}
                onChange={(v) => set("cover_image_url", v)}
                aspect="cover"
                testid="f-cover"
              />
            </div>
          </Section>

          <Section
            label="NARRATIVE · BILINGUAL"
            right={
              <div className="flex gap-2">
                <button className="btn btn-ghost btn-sm" onClick={() => translate("es_en")} disabled={translating} data-testid="translate-es-en">
                  <Languages size={12} /> {translating === "es_en" ? t("common.translating") : t("profile.translate_es_en")}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => translate("en_es")} disabled={translating} data-testid="translate-en-es">
                  <Languages size={12} /> {translating === "en_es" ? t("common.translating") : t("profile.translate_en_es")}
                </button>
              </div>
            }
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="overline">{t("profile.spanish")}</div>
                <FieldBox label={t("profile.tagline")} value={p.tagline_es} onChange={(v) => set("tagline_es", v)} testid="f-tagline-es" />
                <TextareaBox label={t("profile.description")} value={p.description_es} onChange={(v) => set("description_es", v)} testid="f-desc-es" />
              </div>
              <div>
                <div className="overline">{t("profile.english")}</div>
                <FieldBox label={t("profile.tagline")} value={p.tagline_en} onChange={(v) => set("tagline_en", v)} testid="f-tagline-en" />
                <TextareaBox label={t("profile.description")} value={p.description_en} onChange={(v) => set("description_en", v)} testid="f-desc-en" />
              </div>
            </div>
          </Section>

          <Section label="CAPABILITIES & CERTS">
            <FieldBox label={t("profile.capabilities")} value={p.capabilitiesCsv} onChange={(v) => set("capabilitiesCsv", v)} testid="f-capabilities" />
            <FieldBox label={t("profile.certifications")} value={p.certificationsCsv} onChange={(v) => set("certificationsCsv", v)} testid="f-certifications" />
            <FieldBox label={t("profile.industries")} value={p.industriesCsv} onChange={(v) => set("industriesCsv", v)} testid="f-industries" />
          </Section>

          <Section label={t("profile.legalEntity")}>
            <div className="font-mono text-xs mb-3" style={{ color: "var(--ink-2)" }}>
              {t("profile.legalEntityHint")}
            </div>
            <FieldBox label={t("profile.legal_name")} value={p.legal_name} onChange={(v) => set("legal_name", v)} testid="f-legal-name" />
            <div className="grid grid-cols-2 gap-4">
              <FieldBox label={t("profile.rfc")} value={p.rfc} onChange={(v) => set("rfc", v)} testid="f-rfc" />
              <FieldBox label={t("profile.immex_number")} value={p.immex_number} onChange={(v) => set("immex_number", v)} testid="f-immex" />
              <FieldBox label={t("profile.padron_number")} value={p.padron_number} onChange={(v) => set("padron_number", v)} testid="f-padron" />
              <FieldBox label={t("profile.duns_number")} value={p.duns_number} onChange={(v) => set("duns_number", v)} testid="f-duns" />
            </div>
          </Section>
        </div>

        {/* RIGHT */}
        <div className="md:col-span-5 space-y-8">
          <Section label="PLANT SPECS">
            <div className="grid grid-cols-2 gap-4">
              <FieldBox label={t("profile.city")} value={p.city} onChange={(v) => set("city", v)} testid="f-city" />
              <FieldBox label={t("profile.state")} value={p.state} onChange={(v) => set("state", v)} testid="f-state" />
              <FieldBox label={t("profile.country")} value={p.country} onChange={(v) => set("country", v)} testid="f-country" />
              <FieldBox label={t("profile.year_founded")} value={p.year_founded || ""} onChange={(v) => set("year_founded", v)} testid="f-year" type="number" />
              <FieldBox label={t("profile.employees")} value={p.employees || ""} onChange={(v) => set("employees", v)} testid="f-employees" />
              <FieldBox label={t("profile.plant_size")} value={p.plant_size_sqm || ""} onChange={(v) => set("plant_size_sqm", v)} testid="f-plant-size" type="number" />
            </div>
          </Section>

          <Section label="CONTACT">
            <FieldBox label={t("profile.website")} value={p.website} onChange={(v) => set("website", v)} testid="f-website" />
            <FieldBox label={t("profile.contact_email")} value={p.contact_email} onChange={(v) => set("contact_email", v)} testid="f-contact-email" />
            <FieldBox label={t("profile.contact_phone")} value={p.contact_phone} onChange={(v) => set("contact_phone", v)} testid="f-contact-phone" />
          </Section>

          <Section label="GALLERY">
            <GalleryUploader
              label={t("profile.gallery")}
              value={(p.galleryText || "").split("\n").map((s) => s.trim()).filter(Boolean)}
              onChange={(urls) => set("galleryText", urls.join("\n"))}
              testid="f-gallery"
            />
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ label, children, right }) {
  return (
    <div className="cd-card p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="overline">{label}</div>
        {right}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <input className="cd-input mt-2" value={value || ""} type={type} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
function FieldBox({ label, value, onChange, testid, type = "text", required }) {
  return (
    <div>
      <label className="overline">{label}{required ? " *" : ""}</label>
      <input
        className="cd-input-box mt-2"
        value={value || ""}
        type={type}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
      />
    </div>
  );
}
function TextareaBox({ label, value, onChange, testid, rows = 6 }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <textarea
        className="cd-textarea mt-2"
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}


/* iter31 — Real-time verification progress toast. Renders after every
 * profile save:
 *   • 4/4 complete → green celebratory pill ("✓ Profile verified!")
 *   • <4 complete  → orange progress pill ("X of 4 complete — Y more to go!")
 * The "publish" outcome is appended as a quiet trailing chip so owners
 * still see whether the save was a publish vs draft action (legacy
 * "Saved./Published." copy). Auto-dismisses after 6-8s — managed by
 * the parent ProfileEditor via setTimeout.
 */
function VerificationToast({ complete, total, allDone, published, lang }) {
  const remaining = total - complete;
  const headline = allDone
    ? (lang === "es" ? "¡Perfil verificado!" : "Profile verified!")
    : (lang === "es"
        ? `${complete} de ${total} completos — ¡falta${remaining === 1 ? "" : "n"} ${remaining}!`
        : `${complete} of ${total} complete — ${remaining} more to go!`);
  const subline = published
    ? (lang === "es" ? "Publicado" : "Published")
    : (lang === "es" ? "Guardado" : "Saved");
  const accent = allDone ? "var(--green)" : "var(--orange)";
  return (
    <div
      className="mb-6 border p-4 flex items-center gap-3"
      style={{
        borderColor: accent,
        background: allDone ? "rgba(22,163,74,0.06)" : "rgba(255,79,0,0.05)",
        animation: "cd-fade-in 0.25s ease",
      }}
      data-testid="profile-verification-toast"
      data-complete={complete}
      data-all-done={allDone ? "1" : "0"}
    >
      <div
        className="flex items-center justify-center flex-shrink-0"
        style={{
          width: 32, height: 32, borderRadius: 999,
          background: allDone ? "var(--green)" : "var(--orange)",
          color: "#fff",
        }}
      >
        <Check size={18} strokeWidth={3} />
      </div>
      <div className="flex-1">
        <div
          className="font-heading font-semibold text-sm"
          style={{ color: accent }}
          data-testid="profile-verification-toast-headline"
        >
          {headline}
        </div>
        <div className="font-mono text-xs mt-0.5" style={{ color: "var(--ink-2)" }}>
          {subline}
        </div>
      </div>
    </div>
  );
}
